import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBadge from '../common/NotificationBadge';
import TicketCountBadge from '../common/TicketCountBadge';
import { ticketAPI } from '../../services/api';

const {
  FiHome,
  FiUsers,
  FiUserCheck,
  FiFileText,
  FiCalendar,
  FiMessageCircle,
  FiTrendingUp,
  FiBarChart3,
  FiSettings,
  FiMenu,
  FiX,
  FiLogOut,
  FiShield,
  FiPhone,
  FiGlobe,FiMessageSquare
} = FiIcons;

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data } = await ticketAPI.getTickets();
        setOpenTicketCount((data || []).filter(t => t.status !== 'closed').length);
      } catch (_) { /* silently ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const navigation = [
    { name: 'แดชบอร์ด', href: '/admin/dashboard', icon: FiHome },
    { name: 'ผู้ใช้งาน', href: '/admin/users', icon: FiUsers },
    { name: 'ตัวแทน', href: '/admin/agents', icon: FiUserCheck },
    { name: 'ลูกข่าย', href: '/admin/clients', icon: FiUsers },
    { name: 'ปฏิทิน', href: '/admin/calendar', icon: FiCalendar },
    { name: 'ผลการทำงาน', href: '/admin/performance', icon: FiTrendingUp },
    { name: 'รายการแจ้ง', href: '/admin/tickets', icon: FiMessageSquare, showTicketBadge: true }
  ];

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 flex z-40 lg:hidden" 
          role="dialog" 
          aria-modal="true"
        >
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75" 
            onClick={() => setSidebarOpen(false)}
          ></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <SafeIcon icon={FiShield} className="w-6 h-6 text-blue-600 mr-2" />
            <h1 className="text-xl font-bold text-gray-900">ผู้ดูแลระบบ</h1>
          </div>
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <SafeIcon icon={FiX} className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    isActive
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <SafeIcon
                    icon={item.icon}
                    className={`${
                      isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                    } mr-3 flex-shrink-0 h-5 w-5`}
                  />
                  {item.name}
                  {item.showTicketBadge && (
                    <TicketCountBadge count={openTicketCount} className="ml-auto inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[9px] font-bold text-white bg-red-500 rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Admin info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="px-3">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="w-4 h-4 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">Administrator</p>
                  <p className="text-xs text-gray-500">System Admin</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact & Logout */}
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
            <button
              onClick={() => {
                setContactModalOpen(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
            >
              <SafeIcon icon={FiPhone} className="mr-3 h-5 w-5" />
              ติดต่อเรา
            </button>
            <button
              onClick={logout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
            >
              <SafeIcon icon={FiLogOut} className="mr-3 h-5 w-5" />
              ออกจากระบบ
            </button>
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Header */}
        <div className="bg-white shadow border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <SafeIcon icon={FiMenu} className="h-6 w-6" />
              </button>
              <div className="ml-4 lg:ml-0">
                <h1 className="text-lg font-semibold text-gray-900 lg:text-xl">
                  {import.meta.env.VITE_APP_TITLE || 'Demo AMS'} Admin <span className="text-sm font-normal text-gray-500">v{import.meta.env.VITE_APP_VERSION || '1.0.0'}</span>
                </h1>
              </div>
            </div>

            {/* Header actions */}
            <div className="flex items-center space-x-4">
              <NotificationBadge to="/admin/notifications" />
              {user && (
                <div className="flex items-center space-x-2">
                  {/* Profile Image */}
                  {user.pictureUrl ? (
                    <img
                      src={user.pictureUrl}
                      alt={user.displayName || user.name || user.username}
                      className="w-8 h-8 rounded-full border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiShield} className="w-4 h-4 text-blue-600" />
                    </div>
                  )}
                  {/* User Name - Hidden on small screens */}
                  <div className="hidden sm:block">
                    <span className="text-sm font-medium text-gray-700">
                      {user.displayName || user.name || user.username || 'Admin'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6 pb-20 sm:pb-6">
            {children}
          </div>
        </main>
      </div>

      {/* Contact Modal */}
      {contactModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-gray-900 bg-opacity-75" onClick={() => setContactModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-sm text-center" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">ติดต่อเรา</h3>
              <button onClick={() => setContactModalOpen(false)}><SafeIcon icon={FiX} className="w-6 h-6 text-gray-500" /></button>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-center">
                <img 
                  src="/images/contact/ef0a6c8b008252465e687121c4fd9f810266b6b9_temp_tl.jpg" 
                  alt="Contact Us" 
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  onError={(e) => {e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'}}
                />
                <div className="hidden text-gray-500 p-4 border border-gray-200 rounded">Image not found</div>
              </div>
              
              <div className="flex flex-col space-y-3">
                <a 
                  href="https://lin.ee/Y5Caark" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-4 py-2 bg-[#06C755] text-white rounded-lg hover:bg-[#05b64d] transition-colors"
                >
                  <SafeIcon icon={FiMessageCircle} className="mr-2" />
                  ติดต่อผ่าน LINE
                </a>
                
                <a 
                  href="https://99ssconsult.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiGlobe} className="mr-2" />
                  เข้าสู่เว็บไซต์
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom navigation - scrollable admin nav */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
        <div 
          className="flex overflow-x-auto" 
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitScrollbar: { display: 'none' }
          }}
        >
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  isActive ? 'text-blue-600' : 'text-gray-400'
                } flex flex-col items-center py-3 px-3 min-w-[80px] flex-shrink-0`}
              >
                <div className="relative">
                  <SafeIcon icon={item.icon} className="h-4 w-4" />
                  {item.showTicketBadge && (
                    <TicketCountBadge count={openTicketCount} className="absolute -top-1 -right-1 flex items-center justify-center h-3.5 min-w-[14px] px-0.5 text-[8px] font-bold text-white bg-red-500 rounded-full" />
                  )}
                </div>
                <span className="text-xs mt-1 text-center leading-tight">{item.name}</span>
              </Link>
            );
          })}

          <NotificationBadge
            to="/admin/notifications"
            className={`${
              location.pathname === '/admin/notifications' ? 'text-blue-600' : 'text-gray-400'
            } flex flex-col items-center py-3 px-3 min-w-[80px] flex-shrink-0`}
            iconClassName="h-4 w-4"
          >
            <span className="text-xs mt-1 text-center leading-tight">แจ้งเตือน</span>
          </NotificationBadge>
          
          <button
            onClick={() => setContactModalOpen(true)}
            className="text-gray-400 flex flex-col items-center py-3 px-3 min-w-[80px] flex-shrink-0"
          >
            <SafeIcon icon={FiPhone} className="h-4 w-4" />
            <span className="text-xs mt-1 text-center leading-tight">ติดต่อเรา</span>
          </button>

          <div className="flex flex-col items-center justify-center py-3 px-3 min-w-[50px] flex-shrink-0 text-gray-300">
            <span className="text-[10px] font-medium leading-tight">v{import.meta.env.VITE_APP_VERSION || '1.0.0'}</span>
          </div>
        </div>
        <style jsx>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default AdminLayout;
